<template>
  <v-menu bottom offset-y>
    <template #activator="{ props }">
      <v-btn
        color="secondary"
        variant="outlined"
        v-bind="props"
        class="custom-button"
        :loading="loading"
      >
        {{ selectedSiteLabel }}
        <v-icon class="ms-2">mdi-menu-down</v-icon>
      </v-btn>
    </template>

    <v-list class="deployment-list">
      <v-list-item
        v-for="(item, index) in sortedItems"
        :key="index"
        @click="handleClick(item.site)"
        :disabled="item.disabled"
        :class="{
          'default-deployment': item.site === DEFAULT_DEPLOYMENT.site,
        }"
      >
        <v-list-item-title>{{ item.label }}</v-list-item-title>
        <v-list-item-subtitle class="text-caption">
          {{ item.site }}
        </v-list-item-subtitle>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { DEFAULT_DEPLOYMENT } from "@/store";
import { useAuth0 } from "@auth0/auth0-vue";
import { watch } from "vue";
import { setToken } from "@/Service/AuthService";

export default {
  setup() {
    const auth0 = useAuth0();

    watch(
      () => auth0.isAuthenticated.value,
      async (isAuthenticated) => {
        if (isAuthenticated) {
          try {
            const token = await auth0.getAccessTokenSilently({
              audience: process.env.VUE_APP_AUTH0_AUDIENCE,
              scope: "read:all",
            });
            setToken(token);
          } catch (error) {
            console.error("Error getting token in setup:", error);
          }
        }
      },
      { immediate: true }
    );

    return {
      isAuthenticated: auth0.isAuthenticated,
      getAccessTokenSilently: auth0.getAccessTokenSilently,
      auth0,
      DEFAULT_DEPLOYMENT,
    };
  },

  data() {
    return {
      selectedSite: DEFAULT_DEPLOYMENT.site,
      loading: false,
      retryAttempts: 3,
    };
  },

  computed: {
    ...mapGetters([
      "getAuthToken",
      "isAuthenticated",
      "getDeployments",
      "isValidDeployment",
    ]),
    site() {
      return this.$store.state.site;
    },
    items() {
      return this.getDeployments;
    },
   selectedSiteLabel() {
  const item = this.items.find((i) => i.site === this.selectedSite);
  if (!item) {
    if (this.selectedSite === DEFAULT_DEPLOYMENT.site) {
      return DEFAULT_DEPLOYMENT.site;
    }
    return this.selectedSite;
  }
  return item.site; // Now it will return the site instead of the label
},
    sortedItems() {
      // Sort all items alphabetically including the default deployment
      return [...this.items].sort((a, b) => {
        // Sort by label
        return a.label.localeCompare(b.label);
      });
    },
  },

  watch: {
    site: {
      immediate: true,
      handler(newSite) {
        if (newSite && newSite !== this.selectedSite) {
          console.log("Site changed in store:", newSite);
          this.selectedSite = newSite;
        } else if (!newSite) {
          this.selectedSite = DEFAULT_DEPLOYMENT.site;
        }
      },
    },
    selectedSite(newSite) {
      console.log("Selected site changed:", newSite);
      if (this.isValidDeployment(newSite)) {
        console.log("Setting valid site:", newSite);
        this.setSite(newSite);
        this.$emit("siteSelected", newSite);
      } else {
        console.warn("Invalid site selected, reverting to default:", newSite);
        this.selectedSite = DEFAULT_DEPLOYMENT.site;
      }
    },
    isAuthenticated: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.initializeAuth();
        }
      },
    },
  },

  methods: {
    ...mapActions(["setSite", "setAuth", "fetchDeployments"]),

    async initializeAuth() {
      try {
        console.log("Initializing auth...");
        const token = await this.getAccessTokenSilently({
          audience: process.env.VUE_APP_AUTH0_AUDIENCE,
          scope: "read:all",
        });
        console.log("Token received:", token ? "Yes" : "No");

        setToken(token);

        const user = this.auth0.user.value;
        console.log("User info received:", user ? "Yes" : "No");

        await this.setAuth({ token, user });
        console.log("Auth stored in Vuex");

        await this.loadDeployments();
      } catch (error) {
        console.error("Error initializing auth:", error);
        if (error.response) {
          console.error("Response error:", {
            data: error.response.data,
            status: error.response.status,
          });
        }
      }
    },

    async loadDeployments(attempt = 1) {
      if (attempt > this.retryAttempts) {
        console.error("Max retry attempts reached");
        return;
      }

      this.loading = true;
      try {
        console.log("Loading deployments, attempt:", attempt);
        await this.fetchDeployments();
        console.log("Deployments loaded:", this.items.length);

        if (!this.isValidDeployment(this.selectedSite)) {
          console.log("Current selection not found, using default deployment");
          await this.handleClick(DEFAULT_DEPLOYMENT.site);
        }
      } catch (error) {
        console.error(`Error loading deployments (attempt ${attempt}):`, error);
        if (attempt < this.retryAttempts) {
          console.log(`Retrying in 2 seconds... (attempt ${attempt + 1})`);
          setTimeout(() => this.loadDeployments(attempt + 1), 2000);
        }
      } finally {
        this.loading = false;
      }
    },

    async handleClick(site) {
      console.log("Deployment selected:", site);
      if (site === this.selectedSite) return;

      if (this.isValidDeployment(site)) {
        console.log("Setting valid deployment:", site);
        try {
          await this.setSite(site);
          this.selectedSite = site;
        } catch (error) {
          console.error("Error setting site:", error);
          this.selectedSite = DEFAULT_DEPLOYMENT.site;
        }
      } else {
        console.warn("Invalid deployment selected:", site);
        this.selectedSite = DEFAULT_DEPLOYMENT.site;
      }
    },
  },

  async mounted() {
    console.log("Site selector mounted");
    if (!this.site) {
      this.selectedSite = DEFAULT_DEPLOYMENT.site;
    }
    if (this.isAuthenticated) {
      await this.initializeAuth();
    }
  },
};
</script>

<style scoped>
.custom-button {
  background-color: #30aec0 !important;
  color: white !important;
  padding: 8px 16px;
  margin-right: 16px;
}

.deployment-list {
  max-height: 400px !important;
  overflow-y: auto !important;
}

.v-list-item-subtitle {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
}

.default-deployment {
  background-color: rgba(48, 174, 192, 0.1);
}

.deployment-list::-webkit-scrollbar {
  width: 8px;
}

.deployment-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.deployment-list::-webkit-scrollbar-thumb {
  background: #30aec0;
  border-radius: 4px;
}

.deployment-list::-webkit-scrollbar-thumb:hover {
  background: #2b9cad;
}

@media (max-width: 600px) {
  .custom-button {
    font-size: 11px;
  }

  .v-list-item-subtitle {
    font-size: 0.7rem;
  }

  .deployment-list {
    max-height: 300px !important;
  }
}
</style>